import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchMembersByDepartment, releaseMemberFromDepartment } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const memberClassLabel = (cls, t) => {
  switch (cls) {
    case "Collaborator": return t("member.class.Collaborator");
    case "Specialist": return t("member.class.Specialist");
    case "Investor": return t("member.class.Investor");
    case "ProductUser": return t("member.class.ProductUser");
    case "CompanyMember": return t("member.class.CompanyMember");
    default: return cls || "";
  }
};

const memberStateLabel = (state, t) => {
  switch (state) {
    case "Registered": return t("member.state.Registered");
    case "Assigned": return t("member.state.Assigned");
    case "Released": return t("member.state.Released");
    default: return state || "";
  }
};

export default function BuildDepartmentMemberDetailPage() {
  const { businessDomainId, businessUnitId, departmentId, memberId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const memberListPath = `/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/members`;

  const loadData = useCallback(async () => {
    try {
      const members = await fetchMembersByDepartment(departmentId);
      const found = (Array.isArray(members) ? members : []).find(
        (m) => String(m.memberId) === String(memberId)
      );
      setData(found || null);
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRelease = async () => {
    try {
      await releaseMemberFromDepartment(memberId);
      notify.success(t("message.release.success"));
      navigate(memberListPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const fullName = `${data.lastName || ""} ${data.firstName || ""}`.trim();

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("member.detail.title")}</Typography>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("member.name")} value={fullName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.email")} value={data.email} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.class")} value={memberClassLabel(data.memberClass, t)} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.state")} value={memberStateLabel(data.memberState, t)} />
          </Grid>
          {data.memberDescription && (
            <Grid size={12}>
              <DetailField label={t("common.description")} value={data.memberDescription} />
            </Grid>
          )}
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button variant="contained" color="error" onClick={() => setConfirmOpen(true)}>
          {t("member.release")}
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/members`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleRelease}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
